import type {
  EncoderName,
  RecordingCaptureMouseCursor,
  RecordingDepthGame,
  RecordingPointerTrackingType,
  RecordingResolutionMode,
} from "domains/managingJson/store/types"

// Numeric clamp ranges — must match the desktop client's validator exactly.
// Intentionally permissive so experimentation isn't bottlenecked by the admin form;
// these are sanity floors/ceilings, not workflow limits. The desktop defaults stay
// sensible. Source of truth on the desktop side: buff.app/src/services/streaming/recordingConfig/validator.ts
export const CLAMPS = {
  fps: { min: 1, max: 480 },
  width: { min: 320, max: 7680 }, // up to 8K width
  height: { min: 240, max: 4320 }, // up to 8K height
  maxKbps: { min: 100, max: 1_000_000 }, // up to 1 Gbps
  volume: { min: 0, max: 100 }, // percentage — hard semantic limit
  heartbeatIntervalMs: { min: 100, max: 600_000 }, // 100ms to 10min
  mousePosHz: { min: 1, max: 1000 }, // up to 1kHz polling
  // 50 MiB floor (anything smaller is meaningless overhead — each part needs an IDR + container
  // headers), 10 GiB ceiling. Clearing the field stores null, meaning "no splitting".
  maxFilePartSizeBytes: { min: 50 * 1024 * 1024, max: 10 * 1024 * 1024 * 1024 },
  keyframeIntervalSec: { min: 1, max: 120 }, // up to 2min keyframe interval
  quotaPctOfFree: { min: 1, max: 100 }, // percentage of free disk space
  minFreeGbToRecord: { min: 0, max: 1000 }, // matches desktop validator
  pointerTrackingMousePosRate: { min: 1, max: 2000 }, // matches desktop validator
  pointerTrackingGamepadRate: { min: 1, max: 2000 },
  pointerTrackingTimesyncRate: { min: 1, max: 60 },
  inGameSpeedPct: { min: 1, max: 100 }, // percentage of bandwidth baseline
  // Upload tuning — wire units (bytes / ms), matching the desktop validator's clampNumber ranges.
  // The form edits partSizeBytes in MiB and s3AbortWindowMs in hours; see getValueProps/normalize.
  partSizeBytes: { min: 5 * 1024 * 1024, max: 100 * 1024 * 1024 }, // 5–100 MiB
  maxPartRetries: { min: 0, max: 10 },
  retryBackoffMs: { min: 100, max: 30_000 },
  retryBackoffCapMs: { min: 1_000, max: 120_000 },
  s3AbortWindowMs: { min: 60 * 60 * 1000, max: 30 * 24 * 60 * 60 * 1000 }, // 1 h – 30 d
  maxJobRetries: { min: 0, max: 10 },
  jobRetryBaseDelayMs: { min: 1_000, max: 10 * 60 * 1000 },
  jobRetryCapMs: { min: 1_000, max: 30 * 60 * 1000 },
  maxLaunchRevives: { min: 0, max: 10 },
  networkHoldProbeMs: { min: 5_000, max: 120_000 },
  maxOfflineHolds: { min: 0, max: 100 },
  maxOfflineHoldMs: { min: 60 * 1000, max: 24 * 60 * 60 * 1000 }, // 1 min – 24 h
  depthFps: { min: 1, max: 60 },
  depthSize: { min: 0, max: 16384 }, // 0 = native
  depthMinFreeGb: { min: 0, max: 1000 },
  depthChunkMinutes: { min: 1, max: 60 },
} as const

export const HOSTNAME_PATTERN
  = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i

export const DEPTH_RULES: { value: RecordingDepthGame["rule"]; label: string }[] = [
  { value: "most_draws", label: "Most draws (default)" },
  { value: "most_clears", label: "Most clears" },
]
export const DEPTH_CODECS: { value: RecordingDepthGame["codec"]; label: string }[] = [
  { value: "ffv1", label: "16-bit codes, lossless (FFV1)" },
  { value: "raw", label: "32-bit raw, uncompressed" },
  { value: "zstd", label: "32-bit, lossless (zstd)" },
  { value: "bdc", label: "32-bit, lossless (bdc)" },
]
export const DEPTH_BITS: { value: RecordingDepthGame["bits"]; label: string }[] = [
  { value: 16, label: "16 (finest)" },
  { value: 12, label: "12 (half the size)" },
  { value: 10, label: "10 (smallest)" },
]
export const DEPTH_CAMERA_POSE_PROVIDERS: { value: NonNullable<RecordingDepthGame["cameraPoseProvider"]>; label: string }[] = [
  { value: "unturned", label: "Unturned module" },
  { value: "repo", label: "R.E.P.O BepInEx plugin" },
]

export const ENCODER_NAMES: EncoderName[] = [
  "NVIDIA_NVENC_NEW",
  "NVIDIA_NVENC",
  "NVIDIA_NVENC_HEVC",
  "NVIDIA_NVENC_AV1",
  "AMD_AMF",
  "AMD_AMF_HEVC",
  "INTEL",
  "X264",
]

export const CAPTURE_MOUSE_CURSOR_VALUES: RecordingCaptureMouseCursor[] = [
  "none",
  "both",
  "gameOnly",
  "desktopOnly",
]

export const RESOLUTION_MODE_VALUES: RecordingResolutionMode[] = ["forced", "native"]

export const POINTER_TRACKING_TYPES: RecordingPointerTrackingType[] = ["efficient", "inefficient"]

// OW encoder presets per family. Must match desktop client's clamps.ts.
export const NVIDIA_PRESETS = [
  "AUTOMATIC",
  "DEFAULT",
  "HIGH_QUALITY",
  "HIGH_PERFORMANCE",
  "BLURAY_DISK",
  "LOW_LATENCY",
  "HIGH_PERFORMANCE_LOW_LATENCY",
  "HIGH_QUALITY_LOW_LATENCY",
  "LOSSLESS",
  "HIGH_PERFORMANCE_LOSSLESS",
]

export const AMD_PRESETS = ["AUTOMATIC", "BALANCED", "SPEED", "QUALITY", "ULTRA_LOW_LATENCY", "LOW_LATENCY"]

export const X264_PRESETS = [
  "ULTRAFAST",
  "SUPERFAST",
  "VERYFAST",
  "FASTER",
  "FAST",
  "MEDIUM",
  "SLOW",
  "SLOWER",
  "VERYSLOW",
  "PLACEBO",
]

export const NVIDIA_RATE_CONTROL = ["RC_CBR", "RC_CQP", "RC_VBR", "RC_VBR_MINQP", "RC_2_PASS_QUALITY"]
export const AMD_RATE_CONTROL = ["RC_CBR", "RC_CQP", "RC_VBR", "RC_VBR_MINQP"]
export const X264_RATE_CONTROL = ["RC_CBR", "RC_CQP", "RC_VBR"]

export type EncoderFamily = "nvidia" | "amd" | "intel" | "x264"

export function encoderFamily(name: EncoderName): EncoderFamily {
  if (name.startsWith("NVIDIA_NVENC")) return "nvidia"
  if (name.startsWith("AMD_AMF")) return "amd"
  if (name === "INTEL") return "intel"

  return "x264"
}

export function presetsForFamily(family: EncoderFamily): string[] {
  switch (family) {
  case "nvidia": return NVIDIA_PRESETS
  case "amd": return AMD_PRESETS
  case "x264": return X264_PRESETS
  case "intel": return [""]
  }
}

export function rateControlsForFamily(family: EncoderFamily): string[] {
  switch (family) {
  case "nvidia": return NVIDIA_RATE_CONTROL
  case "amd": return AMD_RATE_CONTROL
  case "x264": return X264_RATE_CONTROL
  case "intel": return [""]
  }
}
